import { useEffect, useId, useState } from 'react';
import {
  EVENT_STATUSES,
  type ModerationItemStatus,
  type ModerationQueueItemDto,
} from '@icebreaker/shared';
import { type TranslationKey, type Translator } from '@icebreaker/shared/i18n';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { Button, EmptyState, ErrorBox, Seg, Skeleton, Tag } from '@icebreaker/ui';
import { Field, Textarea } from '@icebreaker/ui';
import {
  decideModeration,
  fetchQueue,
  fetchQueueItem,
  mutationReset,
  queueItemSelected,
} from '../store/admin.slice';
import { subSurfacePermission } from '@/app/screen-permissions';
import { PermissionSurfaceGate } from '@/features/access/ui/permission-gate';
import { ClaimDecisionPanel } from './claim-decision-panel';
import styles from './admin.module.css';

const PAGE_SIZE = 50;
const MIN_REASON_LENGTH = 20;
type StatusFilter = ModerationItemStatus | 'all';

/**
 * Moderasyon kuyruğu (tasarım ekran 26): sol sütunda tek kuyruk (rapor + şüpheli
 * etkinlik + itiraz + sahiplenme), sağda karar panosu. Her karar ZORUNLU gerekçe
 * ister; gerekçe denetim kaydına yazılır ve iki tarafa gösterilir.
 */
export function ModerationScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const queue = useAppSelector((state: RootState) => state.admin.queue);
  const detail = useAppSelector((state: RootState) => state.admin.detail);
  const selectedItemId = useAppSelector((state: RootState) => state.admin.selectedItemId);
  const mutation = useAppSelector((state: RootState) => state.admin.mutation);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const reasonId = useId();

  useEffect(() => {
    dispatch(fetchQueue.request({ status: statusFilter, page: 1, pageSize: PAGE_SIZE }));
  }, [dispatch, statusFilter]);

  useEffect(() => {
    if (selectedItemId) {
      dispatch(fetchQueueItem.request(selectedItemId));
      setReason('');
      setTouched(false);
      dispatch(mutationReset());
    }
  }, [dispatch, selectedItemId]);

  // Karar başarılı → gerekçe alanı sıfırlanır (kalem slice'ta yerinde güncellendi).
  useEffect(() => {
    if (mutation.phase === 'succeeded') {
      setReason('');
      setTouched(false);
    }
  }, [mutation.phase]);

  const item = detail.item;
  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;
  const itemOpen = item !== null && (item.status === 'open' || item.status === 'info_requested');
  const isClaim = item?.itemType === 'claim';

  const decide = (decision: 'approved' | 'rejected' | 'info_requested') => {
    setTouched(true);
    if (!item || tooShort) return;
    dispatch(decideModeration.request({ itemId: item.id, input: { decision, reason: trimmed } }));
  };

  return (
    <section className={styles.screen}>
      <h1 className={styles.title}>{t.t('admin.moderation.title')}</h1>
      <p className={styles.lead}>{t.t('admin.moderation.lead')}</p>

      <div style={{ marginTop: 'var(--ib-space-4)' }}>
        <Seg<StatusFilter>
          ariaLabel={t.t('admin.moderation.statusFilterLabel')}
          value={statusFilter}
          onChange={setStatusFilter}
          items={(['open', 'info_requested', 'resolved', 'dismissed', 'all'] as const).map(
            (status) => ({
              value: status,
              label: t.t(`admin.moderation.statuses.${status}` as TranslationKey),
            }),
          )}
        />
      </div>

      <ErrorBox error={queue.error} className={styles.errorBox}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            dispatch(fetchQueue.request({ status: statusFilter, page: 1, pageSize: PAGE_SIZE }))
          }
        >
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      {queue.phase === 'loading' && queue.items.length === 0 ? (
        <div style={{ marginTop: 22 }}>
          <Skeleton label={t.t('a11y.loadingContent')} lines={4} lineHeight={56} />
        </div>
      ) : queue.items.length === 0 && queue.phase === 'succeeded' ? (
        <div style={{ marginTop: 22 }}>
          <EmptyState
            title={t.t('admin.moderation.emptyTitle')}
            body={t.t('admin.moderation.emptyBody')}
            steps={[
              { title: t.t('admin.moderation.emptyStepSignals') },
              { title: t.t('admin.moderation.emptyStepReports') },
            ]}
          />
        </div>
      ) : (
        <div className={styles.modGrid}>
          <div>
            {queue.items.map((entry) => {
              const active = entry.id === selectedItemId;
              return (
                <button
                  key={entry.id}
                  type="button"
                  className={[styles.queueItem, active ? styles.queueItemActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={active}
                  onClick={() => dispatch(queueItemSelected(entry.id))}
                >
                  <span className={styles.queueItemTop}>
                    <Tag variant={active ? 'accent' : 'neutral'}>
                      {t.t(`admin.moderation.types.${entry.itemType}` as TranslationKey)}
                    </Tag>
                    {entry.priority === 'high' ? (
                      <Tag variant="warning">{t.t('admin.moderation.priorityHigh')}</Tag>
                    ) : null}
                    <span className={styles.queueItemAge}>{formatAge(t, entry.createdAt)}</span>
                  </span>
                  <span className={styles.queueItemTitle}>{entry.target.label || '—'}</span>
                  <span className={styles.queueItemMeta}>{itemMeta(t, entry)}</span>
                </button>
              );
            })}
          </div>

          <aside className={styles.detailPanel}>
            {detail.phase === 'loading' || item === null ? (
              detail.phase === 'failed' && detail.error ? (
                <ErrorBox error={detail.error} className={styles.errorBox} />
              ) : selectedItemId ? (
                <Skeleton label={t.t('a11y.loadingContent')} lines={6} lineHeight={18} />
              ) : (
                <p className={styles.detailBody}>{t.t('admin.moderation.selectPrompt')}</p>
              )
            ) : (
              <>
                <div className={styles.detailKicker}>
                  {t.t(`admin.moderation.types.${item.itemType}` as TranslationKey)}
                </div>
                <h2 className={styles.detailTitle}>{item.target.label || '—'}</h2>
                <div className={styles.detailBody}>{itemMeta(t, item)}</div>

                {/* Claim kaleminde olgular karar panosunda daha zengin gösterilir. */}
                {!(isClaim && itemOpen) && item.facts.length > 0 ? (
                  <div className={styles.factList}>
                    {item.facts.map((fact) => (
                      <div key={fact.key} className={styles.factRow}>
                        <span className={styles.factKey}>
                          {t.t(`admin.moderation.facts.${fact.key}` as TranslationKey)}
                        </span>
                        <span className={styles.factValue}>{formatFact(t, fact)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}

                {item.reportNotes.length > 0 ? (
                  <>
                    <div className={styles.detailKicker} style={{ marginTop: 'var(--ib-space-4)' }}>
                      {t.t('admin.moderation.reportNotes')}
                    </div>
                    <ul className={styles.reportNotes}>
                      {item.reportNotes.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  </>
                ) : null}

                {isClaim && itemOpen ? (
                  /*
                   * Blok 6 dikişi: claim kalemi salt bilgi DEĞİL — karar panosu.
                   *
                   * YETKİ KAPISI (QA S2/S3 · P2-1 · sınıfın ikinci örneği):
                   * kuyruğu GÖRMEK (`staff:moderate`) sahiplenme kararını
                   * VERMEYE yetmez — pano `panel/claims` uçlarını sürer ve
                   * onlar `staff:claim_decide` ister. Kapı olmadan `moderator`
                   * panoyu görüyor, pano açılışta `fetchClaim` atıyor ve ekran
                   * 403'ün üzerine hata kutusu çiziyordu. Kapı MONTAJI engeller:
                   * yetkisiz oturumda istek HİÇ çıkmaz.
                   */
                  <PermissionSurfaceGate permission={subSurfacePermission('panel:claim-decision')}>
                    <ClaimDecisionPanel claimId={item.target.id} queueItemId={item.id} />
                  </PermissionSurfaceGate>
                ) : itemOpen ? (
                  <>
                    <div className={styles.decisionField}>
                      <Field
                        id={reasonId}
                        label={t.t('admin.moderation.decisionLabel')}
                        error={touched && tooShort ? t.t('admin.users.reasonTooShort') : undefined}
                      >
                        <Textarea
                          id={reasonId}
                          rows={3}
                          value={reason}
                          placeholder={t.t('admin.moderation.decisionPlaceholder')}
                          invalid={touched && tooShort}
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </Field>
                    </div>
                    <ErrorBox error={mutation.error} className={styles.errorBox} />
                    <div className={styles.decisionActions}>
                      <Button
                        variant="primary"
                        loading={mutation.phase === 'loading'}
                        loadingLabel={t.t('admin.moderation.deciding')}
                        onClick={() => decide('approved')}
                      >
                        {t.t('admin.moderation.approve')}
                      </Button>
                      <Button
                        variant="dangerOutline"
                        disabled={mutation.phase === 'loading'}
                        onClick={() => decide('rejected')}
                      >
                        {t.t('admin.moderation.reject')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={mutation.phase === 'loading'}
                        onClick={() => decide('info_requested')}
                      >
                        {t.t('admin.moderation.requestInfo')}
                      </Button>
                    </div>
                    <div className={styles.auditNote}>{t.t('admin.moderation.auditNote')}</div>
                  </>
                ) : (
                  <p className={styles.decidedNote}>
                    {t.t('admin.moderation.decidedNote', {
                      decision: t.t(`admin.moderation.statuses.${item.status}` as TranslationKey),
                      date: item.decidedAt ? t.formatDate(item.decidedAt) : '—',
                    })}
                    {item.decisionReason ? ` — ${item.decisionReason}` : ''}
                  </p>
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}

/** Liste meta satırı: kaynak + bildirim sayısı + sebep + hedef referansı. */
function itemMeta(t: Translator, item: ModerationQueueItemDto): string {
  const parts: string[] = [t.t(`admin.moderation.sources.${item.source}` as TranslationKey)];
  if (item.reportCount > 0) {
    parts.push(t.t('admin.moderation.reportCount', { count: item.reportCount }));
  }
  if (item.reason) {
    parts.push(t.t(`admin.moderation.reasons.${item.reason}` as TranslationKey));
  }
  if (item.target.reference) {
    parts.push(item.target.reference);
  }
  return parts.join(' · ');
}

function formatAge(t: Translator, createdAt: string): string {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
  if (days > 0) return t.formatRelativeDays(-days);
  return t.formatDate(createdAt, { timeStyle: 'short' });
}

/** Olgu değerleri: ISO tarih görünümü yerelleştirilir, sayı/etiketler aynen geçer. */
function formatFact(t: Translator, fact: { key: string; value: string }): string {
  if (/^\d{4}-\d{2}-\d{2}T/.test(fact.value)) {
    return t.formatDate(fact.value, { dateStyle: 'medium' });
  }
  if (fact.key === 'eventOrigin') {
    return t.t(
      `admin.moderation.origins.${fact.value === 'shadow' ? 'shadow' : 'organizer'}` as TranslationKey,
    );
  }
  if (fact.key === 'eventStatus') {
    // Ham enum ("published") yerine yerelleştirilmiş etiket; bilinmeyen değer aynen geçer.
    return (EVENT_STATUSES as readonly string[]).includes(fact.value)
      ? t.t(`admin.overview.statuses.${fact.value}` as TranslationKey)
      : fact.value;
  }
  return fact.value;
}
